import { Discriminators } from '@/constants/insurance-policy-const';
import {
  VehicleCoverage,
  InsurancePolicy,
  ResidentialCoverage,
  PetCoverage,
  getInsuranceTypeDisplayName,
} from '@/interfaces';
import { AddressLookupService } from '@/services/store/address-lookup.service';
import { CommonConfigSettings, SettingCategory, useConfigSettingsStore } from '@/store';
import { differenceInDays, isSameDay, addDays, subHours } from 'date-fns';
import { hasMetFloodAndCycloneMitigationMinimumRequirements } from '../insurance/policy-helper';
import { PolicyStatus } from '@/enums/policy-status.enum';
import { replaceBookmark } from '../bookmark.replace';
import { MotorOptionalBenefits } from '@/enums/motor-optional-benefits.enum';
const logDetails = false;

export interface IInsurancePolicyStatusRules {
  showIsInRenewal: boolean;
  showFutureCancelled: boolean;
  showOverdue: boolean;
  showFutureChange: boolean;
  showPaymentDue: boolean;
  showAddToCover: boolean;
  showPayNowButton: boolean;
  policyActiveNoPaymentDue: boolean;
  renewalFullyPaid: boolean;
  showCycloneBanner: boolean;
  isResponsivePayment: boolean;
  isInRenewalWithNonResponsivePayment: boolean;
  isInRenewalWithRenewalPreferenceSetToOffOrNot: boolean;
  isMultiCoveragePolicy: boolean;
  coverageTitle: string;
  areAllCoverageCodesSame: boolean;
  autoRenewActuallyOff: boolean;
  hasAnyRenewalTermResponse: boolean;
  isValidDelinquency: boolean;
}

export const getPolicyStatusDetails = async (
  insurancePolicy: InsurancePolicy,
  currentDate: Date,
  checkForAddToCover: boolean = true,
  renewalInsurancePolicy: InsurancePolicy | undefined = undefined
): Promise<IInsurancePolicyStatusRules> => {
  const result = {
    showIsInRenewal: insurancePolicy.state.isInRenewal,
    showFutureCancelled: isFutureCancelled(insurancePolicy),
    showOverdue: insurancePolicy.state.isOverdue && insurancePolicy.status != PolicyStatus.CANCELLED,
    showFutureChange: false,
    showPaymentDue: false,
    showReduceMyBill: insurancePolicy.state.canReducePremium,
    showAddToCover: false,
    showPayNowButton: false,
    policyActiveNoPaymentDue: false,
    renewalFullyPaid: false,
    showCycloneBanner: false,
    isResponsivePayment: isResponsivePayment(insurancePolicy),
    isInRenewalWithNonResponsivePayment: !isResponsivePayment(insurancePolicy) && insurancePolicy.state.isInRenewal,
    isMultiCoveragePolicy: insurancePolicy.coverages?.length >= 2,
    coverageTitle: getCoverageTitle(insurancePolicy),
    isInRenewalWithRenewalPreferenceSetToOffOrNot: isInRenewalWithRenewalPreferenceSetToOffOrNot(insurancePolicy),
    areAllCoverageCodesSame: areAllCoverageCodesSame(insurancePolicy),
    autoRenewActuallyOff: false,
    hasAnyRenewalTermResponse: false,
    isValidDelinquency: isValidDelinquency(insurancePolicy),
  } as IInsurancePolicyStatusRules;

  result.hasAnyRenewalTermResponse = insurancePolicy?.renewalTermAcceptance !== undefined; //Only interested if a response has been made whether true or false.

  result.showFutureChange =
    insurancePolicy.state.hasSingleFutureChange ||
    insurancePolicy.state.hasMultipleInSequenceFutureChanges ||
    insurancePolicy.state.hasMultipleOutOfSequenceFutureChanges;

  result.showPaymentDue =
    showPayNowButton(insurancePolicy) ||
    (isFutureCancelledWithinGracePeriod(insurancePolicy, currentDate) && isResponsivePayment(insurancePolicy)) ||
    (result.hasAnyRenewalTermResponse === false && result.isInRenewalWithRenewalPreferenceSetToOffOrNot) ||
    (result.hasAnyRenewalTermResponse === false &&
      (await isRenewalPreferenceChangeAllowed(insurancePolicy)) &&
      (insurancePolicy.currentRenewalPreference != null || insurancePolicy.currentRenewalPreference != undefined));

  result.showPayNowButton = showPayNowButton(insurancePolicy);
  result.policyActiveNoPaymentDue = policyActiveWithNoPaymentDue(
    insurancePolicy.status == PolicyStatus.CANCELLED,
    insurancePolicy
  );
  result.renewalFullyPaid =
    insurancePolicy.state.isInRenewal &&
    isResponsivePayment(insurancePolicy) &&
    !insurancePolicy.renewalTermPaymentDetails.isPaymentDue &&
    !insurancePolicy.renewalTermPaymentDetails.totalAmountDue;

  result.showCycloneBanner = await showCycloneBanner(insurancePolicy, renewalInsurancePolicy);
  if (checkForAddToCover) {
    result.showAddToCover = await showAddToCover(insurancePolicy, renewalInsurancePolicy);
  }

  result.autoRenewActuallyOff =
    insurancePolicy.currentRenewalPreference !== undefined && insurancePolicy.currentRenewalPreference === false;

  return result;
};

const policyActiveWithNoPaymentDue = (isCancelled: boolean, insurancePolicy: InsurancePolicy): boolean => {
  return (
    !isCancelled &&
    !insurancePolicy.state?.isInRenewal &&
    ((!insurancePolicy.state.isPaymentDue && isResponsivePayment(insurancePolicy)) ||
      (!insurancePolicy.state.isOverdue && !isResponsivePayment(insurancePolicy)))
  );
};

export const areAllCoverageCodesSame = (insurancePolicy: InsurancePolicy): boolean => {
  if (insurancePolicy.coverages.length < 2) return false; // or true depending on how you want to treat empty arrays

  const firstCode = insurancePolicy.coverages[0].coverageCode;
  return insurancePolicy.coverages.every((c) => c.coverageCode === firstCode);
};

export const getCoverageTitle = (insurancePolicy: InsurancePolicy, identifier: string = '') => {
  if (insurancePolicy.coverages[0]?.isHome) {
    return (insurancePolicy?.coverages[0] as ResidentialCoverage)?.riskAddress?.formattedLong;
  } else if (insurancePolicy.coverages[0]?.isPet) {
    return `${(insurancePolicy?.coverages[0] as PetCoverage)?.petName}, ${(insurancePolicy?.coverages[0] as PetCoverage)?.petBreed}`;
  } else {
    if (identifier) {
      const coverage = insurancePolicy?.coverages.find(
        (x: any) => x.vehicle?.registrationNumber?.toLowerCase() == identifier?.toLowerCase()
      );
      return (coverage as VehicleCoverage)?.vehicle?.formattedSummary;
    }
    return (insurancePolicy?.coverages[0] as VehicleCoverage)?.vehicle?.formattedSummary;
  }
};

const isFutureCancelled = (insurancePolicy: InsurancePolicy) => {
  return insurancePolicy.state.isFutureCancelled && insurancePolicy.status !== PolicyStatus.CANCELLED;
};

const isResponsivePayment = (insurancePolicy: InsurancePolicy) => {
  return insurancePolicy?.state.isInRenewal
    ? insurancePolicy.renewalTermPaymentDetails?.discriminator == Discriminators.ResponsivePaymentDetails
    : insurancePolicy.currentTermPaymentDetails?.discriminator == Discriminators.ResponsivePaymentDetails;
};

export const isValidDelinquency = (insurancePolicy: InsurancePolicy) => {
  const delinquentMonthly = !insurancePolicy.currentTermPaymentDetails.isAnnual && insurancePolicy.state.isOverdue;
  if (delinquentMonthly && insurancePolicy.currentTermPaymentDetails.overdueInceptionDate) {
    return isValidDelinquencyDate(insurancePolicy.currentTermPaymentDetails.overdueInceptionDate);
  }
  return false;
};

export const isValidDelinquencyDate = (date: Date | null) => {
  if (!date) return false;
  const delinquentDays = differenceInDays(new Date(), date);
  return (delinquentDays >= 0 && delinquentDays <= 9) || (delinquentDays >= 12 && delinquentDays <= 29);
};

export const isWithinCancellationGracePeriod = (insurancePolicy: InsurancePolicy, currentDate: Date): boolean => {
  if (!insurancePolicy.cancellationDate || !insurancePolicy.renewalTermStart) return false;
  const cancellationDate = new Date(insurancePolicy.cancellationDate);
  const renewalTermStart = new Date(insurancePolicy.renewalTermStart);
  return isSameDay(cancellationDate, renewalTermStart) && currentDate <= addDays(cancellationDate, 30);
};

// SSPCI.FR3.8
export const isAutoCancelledPolicyInGracePeriod = (
  insurancePolicy: InsurancePolicy,
  currentDate: Date = new Date()
): boolean => {
  return (
    insurancePolicy.status === PolicyStatus.CANCELLED &&
    insurancePolicy.renewalTermPaymentDetails?.discriminator !== Discriminators.ResponsivePaymentDetails &&
    insurancePolicy.currentRenewalPreference === false &&
    isWithinCancellationGracePeriod(insurancePolicy, currentDate)
  );
};

const showPayNowButton = (insurancePolicy: InsurancePolicy) => {
  return (
    !isFutureCancelled(insurancePolicy) &&
    insurancePolicy.state.isPaymentDue &&
    (isResponsivePayment(insurancePolicy) || isValidDelinquency(insurancePolicy))
  );
};

const isRenewalPreferenceChangeAllowed = async (insurancePolicy: InsurancePolicy) => {
  const commonConfigSettingsStore = useConfigSettingsStore();

  if (
    insurancePolicy.state.isInRenewal &&
    !isResponsivePayment(insurancePolicy) &&
    insurancePolicy.state.isPaymentDue &&
    !insurancePolicy.isBrokerPolicy &&
    !insurancePolicy.currentRenewalPreference
  ) {
    const renewalCutOffHours = (await commonConfigSettingsStore.getSetting(
      CommonConfigSettings.RenewalCutOffHours,
      SettingCategory.CommonSettings
    )) as number;
    if (insurancePolicy.renewalTermStart) {
      const policyChangeCutoff = new Date(insurancePolicy.renewalTermStart);
      subHours(policyChangeCutoff, -renewalCutOffHours);
      return new Date() < policyChangeCutoff;
    }
  }
  return false;
};
const isInRenewalWithRenewalPreferenceSetToOffOrNot = (insurancePolicy: InsurancePolicy) => {
  return (
    !insurancePolicy.state.isFutureCancelled &&
    !insurancePolicy.cancellationDate &&
    insurancePolicy.state.isInRenewal &&
    !insurancePolicy.state.isOverdue &&
    insurancePolicy.renewalTermPaymentDetails?.discriminator == Discriminators.NonResponsivePaymentDetails &&
    insurancePolicy.currentRenewalPreference === false &&
    insurancePolicy.renewalTermPaymentDetails?.isPaymentDue
  );
};

export const isEligibleForUpdate = (insurancePolicy: InsurancePolicy, isCurrentTerm: boolean) => {
  const hasFutureChange =
    insurancePolicy.state.hasSingleFutureChange || insurancePolicy.state.hasMultipleInSequenceFutureChanges;
  const hasFutureRenewalChanges = insurancePolicy.state.policyChangeIssues?.renewalTermHasFutureChange ?? false;
  const hasOutOfSequenceChange = insurancePolicy.state.hasMultipleOutOfSequenceFutureChanges;

  const showForStatuses =
    insurancePolicy.status === PolicyStatus.ACTIVE ||
    insurancePolicy.status === PolicyStatus.OVERDUE ||
    (!isCurrentTerm && insurancePolicy.status === PolicyStatus.IN_RENEWAL);

  const isPolicyEligibleForUpdate =
    showForStatuses && !hasFutureChange && !hasFutureRenewalChanges && !hasOutOfSequenceChange;

  return isPolicyEligibleForUpdate;
};
/**
 * show cyclone banner to get user to fill cyclone form
 * @returns
 */
const showCycloneBanner = async (
  insurancePolicy: InsurancePolicy,
  renewalInsurancePolicy?: InsurancePolicy
): Promise<boolean> => {
  if (insurancePolicy.coverages[0].isHome) {
    const residential = insurancePolicy.coverages[0] as ResidentialCoverage;
    const isCovered = await AddressLookupService.getIsCycloneCovered(
      residential.riskAddress.suburb ?? '',
      residential.riskAddress.postcode ?? '',
      residential.riskAddress.state ?? ''
    );

    if (renewalInsurancePolicy !== undefined) {
      const renewalResidential = renewalInsurancePolicy.coverages[0] as ResidentialCoverage;
      const isRenewalPolicyEligibleForUpdate =
        renewalInsurancePolicy !== undefined ? isEligibleForUpdate(renewalInsurancePolicy, false) : undefined;

      return (
        isRenewalPolicyEligibleForUpdate === true &&
        !hasMetFloodAndCycloneMitigationMinimumRequirements(renewalResidential, isCovered.isCovered) &&
        (insurancePolicy?.renewalTermAcceptance === undefined || insurancePolicy?.renewalTermAcceptance === true)
      );
    }

    const isPolicyEligibleForUpdate = isEligibleForUpdate(insurancePolicy, true);

    return (
      isPolicyEligibleForUpdate && !hasMetFloodAndCycloneMitigationMinimumRequirements(residential, isCovered.isCovered)
    );
  }

  return false;
};

/**
 * if the insurance was set to manual update and has been cancelled within 30 days of renewal than we should be able to reinstate it online
 * @param insurancePolicy
 * @param currentDate
 * @returns
 */
const isFutureCancelledWithinGracePeriod = (insurancePolicy: InsurancePolicy, currentDate: Date) => {
  const isResponsivePayment =
    insurancePolicy.renewalTermPaymentDetails != null
      ? insurancePolicy.renewalTermPaymentDetails?.discriminator == Discriminators.ResponsivePaymentDetails
      : insurancePolicy.currentTermPaymentDetails?.discriminator == Discriminators.ResponsivePaymentDetails;

  if (
    insurancePolicy.renewalTermPaymentDetails != null &&
    (insurancePolicy.cancellationDate || insurancePolicy.state.isFutureCancelled) &&
    !isResponsivePayment &&
    !insurancePolicy.isBrokerPolicy &&
    !insurancePolicy.currentRenewalPreference
  ) {
    const result = isWithinCancellationGracePeriod(insurancePolicy, currentDate);

    if (logDetails) {
      console.log('Cancellation date is equal to renewal start date and less than cancellation date plus 30', {
        result: result,
        rule: `cancellationDate: ${insurancePolicy?.cancellationDate} == renewalTermStart: ${insurancePolicy?.renewalTermStart} && currentDate: ${currentDate}`,
      });
    }

    return result;
  }
  return false;
};

const showAddToCover = async (insurancePolicy: InsurancePolicy, renewalInsurancePolicy?: InsurancePolicy) => {
  if (!renewalInsurancePolicy || renewalInsurancePolicy?.coverages?.length === 0) return false;

  const hasMotorComprehensive =
    renewalInsurancePolicy.coverages?.filter((x: any) => x.coverageCode === 'MC')?.length ?? 0 > 0;

  const hasAvailableCoverages = insurancePolicy.coverages.filter((cover: any) => {
    if (cover.coverageCode != 'MC') return false;
    const windscreen = cover.benefits.some((benefit: any) => benefit.toLowerCase() == MotorOptionalBenefits.Windscreen);
    const hireCar = cover.benefits.some((benefit: any) => benefit.toLowerCase() == MotorOptionalBenefits.FreeHireCar);
    return !windscreen || !hireCar;
  });

  const hasAvailableCoveragesForRenewal = renewalInsurancePolicy.coverages.filter((cover: any) => {
    if (cover.coverageCode != 'MC') return false;
    const windscreen = cover.benefits.some((benefit: any) => benefit.toLowerCase() == MotorOptionalBenefits.Windscreen);
    const hireCar = cover.benefits.some((benefit: any) => benefit.toLowerCase() == MotorOptionalBenefits.FreeHireCar);
    return !windscreen || !hireCar;
  });

  //If no options available for current term don't show button return false.
  if (renewalInsurancePolicy.state.isInRenewal && hasMotorComprehensive > 0 && hasAvailableCoverages?.length == 0) {
    return false;
    //If no options available for renewal term don't show button return false.
  } else if (
    renewalInsurancePolicy.state.isInRenewal &&
    hasMotorComprehensive > 0 &&
    hasAvailableCoveragesForRenewal?.length == 0
  ) {
    return false;
  }

  return renewalInsurancePolicy.state.isInRenewal && hasMotorComprehensive > 0;
};

export function getPolicyTitle(dictionary: any, insuranceProduct: any, convertToLowerCase: boolean) {
  const isMultiCoveragePolicy = insuranceProduct.coverages?.length >= 2;
  const areCoverageCodesSame = areAllCoverageCodesSame(insuranceProduct);
  if (isMultiCoveragePolicy) {
    return areCoverageCodesSame
      ? getInsuranceTypeDisplayName(dictionary, insuranceProduct.coverages[0]?.coverageCode, convertToLowerCase)
      : 'Combined Policy';
  }

  return getInsuranceTypeDisplayName(dictionary, insuranceProduct.coverages[0]?.coverageCode);
}

export function getPolicySubTitle(insuranceProduct: any) {
  const isMultiCoveragePolicy = insuranceProduct.coverages?.length >= 2;
  const first = insuranceProduct.coverages[0];

  enum Bookmarks {
    numberOfCoverages = 'numberOfCoverages',
  }

  // Multi coverage logic
  if (isMultiCoveragePolicy) {
    const count = insuranceProduct.coverages.length.toString();

    if (first?.isHome) {
      return replaceBookmark(Bookmarks.numberOfCoverages, '{{numberOfCoverages}} properties', count);
    }

    if (first?.isPet) {
      return replaceBookmark(Bookmarks.numberOfCoverages, '{{numberOfCoverages}} pets', count);
    }

    return replaceBookmark(Bookmarks.numberOfCoverages, '{{numberOfCoverages}} vehicles', count);
  }

  // Single coverage policy
  return getCoverageTitle(insuranceProduct);
}
